import { useRef, useCallback } from 'react';
import {
  getEmailPreviewUrl,
  getApiStreamFallbackUrl,
  getDocumentStreamingUrl,
  getFileType,
  getPreviewFileInfo,
  getPresignedDocumentUrl,
  isDeliberateRefusal,
} from '../components/Document/DocumentUtils';
import {
  getSharePointOfficeExtension,
  officeExtensionFromRecord,
} from '../components/TabView/sharePointOffice';
import {
  bindOrderedBookmarksPanel,
  buildPersistPayload,
  refreshOrderedBookmarksPanel,
  setOrder as setBookmarkOrder,
  splitOrderedPayload,
} from '../components/TabView/orderedBookmarks';

/**
 * Custom hook to handle document loading in WebViewer instances
 * 
 * Features:
 * - Fetches presigned S3 URLs for direct browser-to-S3 loading (fastest path)
 * - Falls back to streaming proxy for non-S3 docs or on error
 * - Handles bookmark import/export
 * - Manages page restoration
 * - Sets up event listeners
 * - No caching - each instance maintains its own state
 */
const useDocumentLoader = () => {
  // Track which documents are currently loading to prevent duplicate loads
  const loadingDocumentsRef = useRef(new Set()); // Set of tabIds currently loading
  
  // Track bookmark import state to prevent re-triggering saves
  const isImportingBookmarksRef = useRef(false);
  
  // Bookmark save timer for debouncing
  const bookmarksSaveTimerRef = useRef(null);

  /**
   * Synchronous fallback: build a proxy streaming URL (no network call).
   */
  const getDocumentUrl = useCallback((documentData) => {
    const documentId = documentData.id 
      || (documentData.unique_id ? documentData.unique_id.substring(1) : null);
    
    if (!documentId) {
      return documentData.s3_path_streaming 
        || documentData.s3_path 
        || documentData.sharepoint_web_url_streaming 
        || documentData.sharepoint_web_url;
    }
    
    return getDocumentStreamingUrl(documentId);
  }, []);

  /**
   * Async: try presigned S3 URL first, fall back to proxy streaming URL.
   */
  const resolveDocumentUrl = useCallback(async (documentData) => {
    const documentId = documentData.id
      || (documentData.unique_id ? documentData.unique_id.substring(1) : null);

    const recordFileType = documentData.file_type
      || String(documentData.name || "").split(".").pop();
    if (documentId && getFileType(recordFileType) === "email") {
      return {
        url: getEmailPreviewUrl(documentId),
        isDirect: false,
        withCredentials: true,
      };
    }

    if (!documentId) {
      return {
        url: documentData.s3_path_streaming
          || documentData.s3_path
          || documentData.sharepoint_web_url_streaming
          || documentData.sharepoint_web_url,
        isDirect: false,
      };
    }

    return getPresignedDocumentUrl(documentId);
  }, []);

  /**
   * Load document in a WebViewer instance
   * Always uses streaming: true (no caching needed)
   */
  const loadDocument = useCallback(async (instance, documentData, options = {}) => {
    const {
      tabId,
      onDocumentLoaded,
      onLoadError,
      fetchBookmarksAPI,
      resolvedUrlPromise,
      isCancelled,
    } = options;

    if (!instance || !documentData) {
      throw new Error('Instance and documentData are required');
    }

    // Prevent duplicate loads
    if (tabId && loadingDocumentsRef.current.has(tabId)) {
      return;
    }

    if (tabId) {
      loadingDocumentsRef.current.add(tabId);
    }

    try {
      // Callers can pass `resolvedUrlPromise` (started BEFORE WebViewer
      // WASM init) so the presigned-URL round trip overlaps instance
      // boot instead of running after it — previously the URL fetch was
      // serialized behind a 1-3s init on every first open. Any failure
      // of the pre-started promise falls back to a fresh resolve so
      // this can never be worse than the old sequential path.
      let urlResult;
      if (resolvedUrlPromise) {
        try {
          urlResult = await resolvedUrlPromise;
        } catch (prefetchError) {
          // A refusal is the backend's settled answer about this document,
          // so re-resolving would only spend the whole polling window a
          // second time before giving the reader the same message.
          if (isDeliberateRefusal(prefetchError)) {
            throw prefetchError;
          }
          console.warn('Prefetched document URL failed, re-resolving:', prefetchError?.message);
          urlResult = null;
        }
      }
      if (!urlResult || !urlResult.url) {
        urlResult = await resolveDocumentUrl(documentData);
      }
      if (typeof isCancelled === "function" && isCancelled()) {
        return;
      }
      const {
        url: documentUrl,
        isDirect,
        withCredentials = false,
        fileName: resolvedFileName,
        contentType,
      } = urlResult;
      if (!documentUrl) {
        throw new Error('No document URL found');
      }

      const { documentViewer } = instance.Core;
      const wantedName = resolvedFileName || documentData.name || '';

      // A leftover document (including a timed-out giant PDF that later
      // finished in the background) must not block the next open.
      const currentDoc = documentViewer.getDocument();
      if (currentDoc) {
        const currentName = (
          (typeof currentDoc.getFilename === 'function' && currentDoc.getFilename())
          || ''
        );
        if (currentName && wantedName && currentName === wantedName) {
          if (tabId) {
            loadingDocumentsRef.current.delete(tabId);
          }
          if (onDocumentLoaded) {
            onDocumentLoaded();
          }
          return;
        }
        try {
          if (typeof documentViewer.closeDocument === 'function') {
            await documentViewer.closeDocument();
          }
        } catch (closeErr) {
          console.warn('Failed to close previous document before load:', closeErr);
        }
      }

      // When we have a presigned S3 URL (isDirect), WebViewer can fetch
      // directly from S3 with no Django proxy hop. Disable streaming so
      // it does a single GET (fastest for typical document sizes).
      // For proxy URLs we keep streaming: true for byte-range support.
      const officeExtension = getSharePointOfficeExtension(documentData);
      const recordOfficeExtension =
        officeExtension || officeExtensionFromRecord(documentData);
      const previewInfo = getPreviewFileInfo({
        fileName: resolvedFileName || documentData.name || documentData.file_type,
        contentType,
      });
      const documentId = documentData.id
        || (documentData.unique_id ? documentData.unique_id.substring(1) : null);

      // Office documents load as Office FIRST — one download, the right
      // parser. Measured live (2026-07-15): 0 of 120 sampled office-named
      // S3 objects contain PDF bytes, so the old PDF-first-then-retry
      // dance was paying two downloads and a guaranteed parse error on
      // every real Word/Excel file. The ladder below is fallback only,
      // not the happy path:
      //   office via primary URL -> office via authenticated API stream
      //   -> pdf via primary URL (guards the hypothetical legacy rename
      //   that left PDF bytes behind an Office suffix).
      const attempts = [];
      if (recordOfficeExtension) {
        attempts.push({
          extension: recordOfficeExtension,
          url: documentUrl,
          credentialed: withCredentials,
        });
        const streamUrl = documentId ? getApiStreamFallbackUrl(documentId) : null;
        if (streamUrl && streamUrl !== documentUrl) {
          attempts.push({
            extension: recordOfficeExtension,
            url: streamUrl,
            credentialed: true,
          });
        }
        attempts.push({
          extension: 'pdf',
          url: documentUrl,
          credentialed: withCredentials,
        });
      } else {
        attempts.push({
          extension: previewInfo.fileType === 'image'
            ? previewInfo.extension
            : 'pdf',
          url: documentUrl,
          credentialed: withCredentials,
        });
      }
      await new Promise((resolve, reject) => {
        let resolved = false;
        let attemptIndex = 0;
        const firstExtension = attempts[0]?.extension;
        const loadTimeoutMs = firstExtension === 'pdf' ? 10 * 60 * 1000 : 30000;
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            try {
              documentViewer.closeDocument?.();
            } catch (closeErr) {
              // Best-effort abort of the in-flight fetch.
            }
            if (onLoadError) {
              onLoadError(new Error('Document load timeout'));
            }
            reject(new Error('Document load timeout'));
          }
        }, loadTimeoutMs);

        const startLoad = ({ extension, url, credentialed }) => {
          if (typeof isCancelled === "function" && isCancelled()) {
            return undefined;
          }
          const pdfFastOpen = extension === 'pdf';
          return instance.UI.loadDocument(url, {
          filename: resolvedFileName || documentData.name || `Document ${documentData.id}`,
          // Apryse's Office viewer needs the complete OOXML/binary file
          // before rendering; byte-range streaming is a PDF optimization.
          streaming: pdfFastOpen ? !isDirect : false,
          extension,
          // Send auth cookies on the authenticated Django stream fallback;
          // presigned S3/CloudFront URLs must stay credential-free.
          withCredentials: credentialed,
          ...(pdfFastOpen ? {
            useDownloader: true,
            chunkSize: 1024 * 1024,
            shouldUseMinimumDownloads: true,
          } : {}),
        });
        };

        const tryNextAttempt = (error) => {
          if (typeof isCancelled === "function" && isCancelled()) {
            return false;
          }
          if (resolved || attemptIndex >= attempts.length - 1) {
            return false;
          }
          attemptIndex += 1;
          const attempt = attempts[attemptIndex];
          console.warn(
            `Document load attempt ${attemptIndex} failed for "${documentData.name}" `
            + `(${String(error?.message || error || 'unknown error').slice(0, 120)}); `
            + `trying extension=${attempt.extension}`
          );
          try {
            // Apryse pops its own "Error Loading Document" modal on a
            // failed parse; close it so the next attempt renders clean.
            if (instance.UI && typeof instance.UI.closeElements === 'function') {
              instance.UI.closeElements(['errorModal']);
            }
            const result = startLoad(attempt);
            if (result && typeof result.catch === 'function') {
              result.catch(onLoadErrorHandler);
            }
            return true;
          } catch (attemptError) {
            return false;
          }
        };

        const onDocumentLoadedHandler = async () => {
          documentViewer.removeEventListener('documentLoaded', onDocumentLoadedHandler);
          if (typeof isCancelled === "function" && isCancelled()) {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              resolve();
            }
            return;
          }
          if (fetchBookmarksAPI && documentData.id) {
            importBookmarks(instance, documentData.id, fetchBookmarksAPI).catch(() => {});
          }

          // Deep-link page jump: AI work-product citations link to
          // `/matter<relative_path>?page=N` so a click from a filed case
          // review (DOCX/PDF/HTML) opens the referenced document at the
          // cited page. Best-effort: a bad/oversized value falls back to
          // the clamped last page; absence of the param changes nothing.
          try {
            const pageParam = parseInt(
              new URLSearchParams(window.location.search).get('page'),
              10
            );
            if (pageParam >= 1 && typeof documentViewer.setCurrentPage === 'function') {
              const totalPages = typeof documentViewer.getPageCount === 'function'
                ? documentViewer.getPageCount()
                : 0;
              const targetPage = totalPages ? Math.min(pageParam, totalPages) : pageParam;
              documentViewer.setCurrentPage(targetPage);
              if (typeof documentViewer.updateView === 'function') {
                documentViewer.updateView();
              }
            }
          } catch (deepLinkError) {
            // A failed page jump must never fail the document load.
          }

          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            if (onDocumentLoaded) {
              onDocumentLoaded();
            }
            resolve();
          } else {
            if (onDocumentLoaded) {
              onDocumentLoaded();
            }
          }
        };

        const onLoadErrorHandler = (error) => {
          if (tryNextAttempt(error)) {
            return;
          }
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            documentViewer.removeEventListener('documentLoaded', onDocumentLoadedHandler);
            if (instance.UI) {
              instance.UI.removeEventListener(instance.UI.Events.LOAD_ERROR, onLoadErrorHandler);
            }
            if (onLoadError) {
              onLoadError(error);
            }
            reject(error);
          }
        };

        documentViewer.addEventListener('documentLoaded', onDocumentLoadedHandler);

        if (instance.UI) {
          instance.UI.addEventListener(instance.UI.Events.LOAD_ERROR, onLoadErrorHandler);
        }

        try {
          const loadResult = startLoad(attempts[attemptIndex]);
          if (loadResult && typeof loadResult.catch === 'function') {
            loadResult.catch(onLoadErrorHandler);
          }
          if (typeof isCancelled === "function" && isCancelled() && !resolved) {
            resolved = true;
            clearTimeout(timeout);
            documentViewer.removeEventListener('documentLoaded', onDocumentLoadedHandler);
            if (instance.UI) {
              instance.UI.removeEventListener(instance.UI.Events.LOAD_ERROR, onLoadErrorHandler);
            }
            resolve();
          }
        } catch (syncError) {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            documentViewer.removeEventListener('documentLoaded', onDocumentLoadedHandler);
            if (instance.UI) {
              instance.UI.removeEventListener(instance.UI.Events.LOAD_ERROR, onLoadErrorHandler);
            }
            reject(syncError);
          }
        }
      });
    } catch (error) {
      console.error(`Failed to load document:`, error);
      throw error;
    } finally {
      if (tabId) {
        loadingDocumentsRef.current.delete(tabId);
      }
    }
  }, [resolveDocumentUrl, getDocumentUrl]);

  const importBookmarks = useCallback(async (instance, documentId, fetchBookmarksAPI) => {
    if (!instance || !documentId || !fetchBookmarksAPI) return;

    const documentViewer = instance.Core?.documentViewer;

    // Run API fetch and "UI ready" wait concurrently so neither adds extra delay
    const [storedBookmarks] = await Promise.all([
      // 1. Fetch stored bookmarks from the API
      fetchBookmarksAPI(documentId).catch((err) => {
        console.warn(`⚠️ Failed to fetch bookmarks for ${documentId}:`, err);
        return null;
      }),

      // 2. Wait for the first page to finish rendering — this is WebViewer's
      //    own signal that the bookmark store is ready to accept imports.
      //    Falls back after 3 s so we never block indefinitely.
      new Promise((resolve) => {
        if (!documentViewer) { resolve(); return; }
        // Already past first render (e.g. same-tab document switch)
        try { if (documentViewer.getCurrentPage() > 0) { resolve(); return; } } catch (_) {}
        const cleanup = () => documentViewer.removeEventListener('finishedRendering', onDone);
        const t = setTimeout(() => { cleanup(); resolve(); }, 3000);
        const onDone = () => { clearTimeout(t); cleanup(); resolve(); };
        documentViewer.addEventListener('finishedRendering', onDone);
      }),
    ]);

    // Ordered bookmarks: the stored payload may carry a display order under
    // the reserved __order__ key. Seed the order store for the custom panel
    // and import only the page-keyed map into WebViewer.
    const { map: storedMap, order: storedOrder } = splitOrderedPayload(storedBookmarks);
    if (storedOrder) {
      setBookmarkOrder(documentId, storedOrder);
    }
    const storedBookmarksMap = storedMap;

    if (!storedBookmarksMap) return;

    const storedHasContent =
      typeof storedBookmarksMap === 'object' && Object.keys(storedBookmarksMap).length > 0;
    if (!storedHasContent) return;

    // Highest 0-based page index in bookmark data — used for streaming re-import
    const bookmarkedPageIndices = Object.keys(storedBookmarksMap)
      .map((k) => Number.parseInt(k, 10))
      .filter((n) => !Number.isNaN(n));
    const maxBookmarkedPageIndex =
      bookmarkedPageIndices.length > 0 ? Math.max(...bookmarkedPageIndices) : -1;

    const applyBookmarks = (bm) => {
      if (!bm) return;
      try {
        if (instance.UI?.importBookmarks) {
          instance.UI.importBookmarks(bm);
        } else if (typeof instance.importBookmarks === 'function') {
          instance.importBookmarks(bm);
        }
      } catch (err) {
        console.warn(`⚠️ Error applying bookmarks:`, err);
      }
    };

    // UI is ready — single import, no retry needed
    isImportingBookmarksRef.current = true;
    try {
      applyBookmarks({});           // clear any stale bookmarks first
      applyBookmarks(storedBookmarksMap);
    } finally {
      setTimeout(() => { isImportingBookmarksRef.current = false; }, 500);
    }
    refreshOrderedBookmarksPanel(instance);

    // Streaming documents: some bookmarked pages may not be in the page list yet
    // (shows as "Page undefined"). Poll getPageCount() and re-import once all
    // bookmarked page indices are available so every label resolves correctly.
    if (maxBookmarkedPageIndex >= 0 && documentViewer) {
      let currentCount = 0;
      try { currentCount = documentViewer.getPageCount(); } catch (_) {}

      if (currentCount <= maxBookmarkedPageIndex) {
        const deadline = Date.now() + 30000;
        const waitAndReImport = () => {
          if (!instance) return;
          let count = 0;
          try { count = documentViewer.getPageCount(); } catch (_) { return; }
          if (count > maxBookmarkedPageIndex) {
            isImportingBookmarksRef.current = true;
            try {
              applyBookmarks({});
              applyBookmarks(storedBookmarksMap);
            } finally {
              setTimeout(() => { isImportingBookmarksRef.current = false; }, 500);
            }
            refreshOrderedBookmarksPanel(instance);
            return;
          }
          if (Date.now() < deadline) setTimeout(waitAndReImport, 500);
        };
        setTimeout(waitAndReImport, 800);
      }
    }
  }, []);

  /**
   * Setup bookmark change listener to persist via API
   */
  const setupBookmarkListener = useCallback((instance, documentId, addBookmarksAPI, dispatch, sendWebSocketMessage, documentDetail, onAutoSave) => {
    if (!instance || !documentId) return null;

    const persistBookmarks = async (showAutoSaveToast = true) => {
      let bookmarks = null;
      if (instance.UI?.exportBookmarks) {
        bookmarks = instance.UI.exportBookmarks();
      } else if (typeof instance.exportBookmarks === 'function') {
        bookmarks = instance.exportBookmarks();
      }

      // Carry the panel's display order (reserved __order__ key) with the
      // page-keyed map so rearranged bookmarks survive reloads.
      bookmarks = buildPersistPayload(documentId, bookmarks);

      // Save to API
      if (addBookmarksAPI) {
        try {
          const bookmarkCount = bookmarks && typeof bookmarks === "object"
            ? Object.keys(bookmarks).length
            : 0;
          console.log(`[BOOKMARKS] Saving for doc ${documentId} with ${bookmarkCount} entries`);
          await addBookmarksAPI(documentId, bookmarks);
          console.log(`[BOOKMARKS] Saved successfully for doc ${documentId}`);
          const documentName = documentDetail?.name || documentDetail?.display_name || documentDetail?.file_name;
          if (showAutoSaveToast && onAutoSave) {
            onAutoSave(documentName || "Document");
          }
        } catch (err) {
          console.error(`[BOOKMARKS] Save failed for doc ${documentId}:`, err);
        }
      }

      // Send WebSocket message if available
      if (dispatch && sendWebSocketMessage) {
        try {
          const documentIdForWS = documentDetail?.id || documentId;
          const bookmarkPayload = {
            doc_id: String(documentIdForWS),
            bookmarks: Array.isArray(bookmarks) ? bookmarks : (bookmarks ? [bookmarks] : []),
          };
          dispatch(sendWebSocketMessage({
            type: 'bookmark_change',
            document_id: Number(documentIdForWS),
            doc_id: bookmarkPayload.doc_id,
            bookmarks: bookmarkPayload.bookmarks,
          }));
        } catch (err) {
          console.error('[WS] Failed to send bookmark_change:', err);
        }
      }
    };

    const schedulePersist = () => {
      // Debounce: bulk delete fires multiple events - batch into single API call
      if (bookmarksSaveTimerRef.current) {
        clearTimeout(bookmarksSaveTimerRef.current);
      }

      bookmarksSaveTimerRef.current = setTimeout(async () => {
        bookmarksSaveTimerRef.current = null;
        await persistBookmarks(true);
      }, 400);
    };

    const bookmarksChangedHandler = () => {
      if (isImportingBookmarksRef.current) return;
      refreshOrderedBookmarksPanel(instance);
      schedulePersist();
    };

    // Custom ordered panel: reorders don't change WebViewer's bookmark
    // store (no userBookmarksChanged), so the panel persists through the
    // same debounced path directly.
    const unbindPanel = bindOrderedBookmarksPanel(instance, documentId, schedulePersist);

    // Attach listener
    if (instance.UI?.addEventListener) {
      instance.UI.addEventListener('userBookmarksChanged', bookmarksChangedHandler);
      return () => {
        // Flush pending bookmark debounce before unmount/close so latest edits persist.
        if (bookmarksSaveTimerRef.current) {
          clearTimeout(bookmarksSaveTimerRef.current);
          bookmarksSaveTimerRef.current = null;
          persistBookmarks(false).catch(() => {});
        }
        if (unbindPanel) unbindPanel();
        if (instance.UI?.removeEventListener) {
          instance.UI.removeEventListener('userBookmarksChanged', bookmarksChangedHandler);
        }
      };
    }

    if (unbindPanel) unbindPanel();
    return null;
  }, []);

  /**
   * Restore page number for a document
   * For MS files, waits for pages to be loaded lazily using pageComplete event
   * 
   * @param {Object} instance - WebViewer instance
   * @param {number} targetPage - Page number to navigate to (1-based)
   * @param {string} fileType - File type from getFileType(): "pdf", "office", or "unknown"
   */
  const restorePage = useCallback((instance, targetPage, fileType) => {
    if (!instance || !targetPage || targetPage <= 0) return;

    const { documentViewer } = instance.Core;
    if (!documentViewer) return;

    // getFileType returns "pdf", "office", or "unknown"
    const isPDF = fileType === "pdf";
    const isMSFile = fileType === "office";

    if (isPDF) {
      // PDFs can navigate immediately - pages are all available
      // But also lock zoom to prevent glitches during navigation
      const lockZoom = () => {
        try {
          if (instance.UI?.setZoomLevel) {
            instance.UI.setZoomLevel(1.0);
            console.log('🔍 [PDF] Locked zoom to 100% during page restoration');
          }
        } catch (e) {
          console.warn('⚠️ Could not lock zoom:', e);
        }
      };
      
      // Lock zoom before navigation
      lockZoom();
      
      setTimeout(() => {
        const totalPages = documentViewer.getPageCount();
        if (totalPages === 0) return;

        const finalPage = Math.min(targetPage, totalPages);
        
        // Maintain zoom lock during navigation
        lockZoom();
        
        documentViewer.setCurrentPage(finalPage);
        documentViewer.updateView();
        console.log(`📄 [PDF] Navigated to last viewed page: ${finalPage}`);
        
        // Lock zoom one more time after navigation to prevent any glitches
        setTimeout(() => {
          lockZoom();
        }, 100);
      }, 50);
    } else if (isMSFile) {
      // MS Office files load pages lazily - need to wait for pageComplete event
      // Also lock zoom to 100% to prevent zoom glitches during page loading
      let navigated = false;
      let cleanupTimeout = null;
      
      // Lock zoom to 100% to prevent auto-fit zoom glitches
      const lockZoom = () => {
        try {
          if (instance.UI?.setZoomLevel) {
            instance.UI.setZoomLevel(1.0);
            console.log('🔍 [MS File] Locked zoom to 100% during page restoration');
          }
        } catch (e) {
          console.warn('⚠️ Could not lock zoom:', e);
        }
      };
      
      // Lock zoom immediately
      lockZoom();
      
      const tryNavigate = () => {
        const totalPages = documentViewer.getPageCount();
        
        // Only navigate if targetPage has been loaded/rendered
        if (!navigated && totalPages > 0 && targetPage <= totalPages) {
          navigated = true;
          const finalPage = Math.min(targetPage, totalPages);
          
          // Maintain zoom lock during navigation
          lockZoom();
          
          documentViewer.setCurrentPage(finalPage);
          documentViewer.updateView();
          console.log(`📄 [MS File] Navigated to last viewed page: ${finalPage}`);
          
          // Lock zoom one more time after navigation to prevent any glitches
          setTimeout(() => {
            lockZoom();
          }, 100);
          
          // Cleanup listener once navigation succeeds
          documentViewer.removeEventListener('pageComplete', tryNavigate);
          if (cleanupTimeout) {
            clearTimeout(cleanupTimeout);
            cleanupTimeout = null;
          }
        } else if (!navigated && totalPages > 0) {
          // Pages are loading but target page not ready yet - maintain zoom lock
          lockZoom();
        }
      };

      // Listen for pageComplete events (fires when each page finishes loading)
      documentViewer.addEventListener('pageComplete', tryNavigate);
      
      // Also try immediately in case pages are already loaded
      setTimeout(() => {
        tryNavigate();
        
        // Fallback: if still not navigated after 5 seconds, try one more time
        // This handles edge cases where pageComplete might not fire
        if (!navigated) {
          cleanupTimeout = setTimeout(() => {
            if (!navigated) {
              const totalPages = documentViewer.getPageCount();
              if (totalPages > 0 && targetPage <= totalPages) {
                navigated = true;
                const finalPage = Math.min(targetPage, totalPages);
                
                // Lock zoom before navigation
                lockZoom();
                
                documentViewer.setCurrentPage(finalPage);
                documentViewer.updateView();
                console.log(`📄 [MS File] Fallback navigation to page: ${finalPage}`);
                
                // Lock zoom one more time after navigation to prevent any glitches
                setTimeout(() => {
                  lockZoom();
                }, 100);
              }
              documentViewer.removeEventListener('pageComplete', tryNavigate);
              cleanupTimeout = null;
            }
          }, 5000);
        }
      }, 100);
    } else {
      // Unknown file type - try default approach
      setTimeout(() => {
        const totalPages = documentViewer.getPageCount();
        if (totalPages === 0) return;

        const finalPage = Math.min(targetPage, totalPages);
        documentViewer.setCurrentPage(finalPage);
        documentViewer.updateView();
        console.log(`📄 [Unknown] Navigated to page: ${finalPage}`);
      }, 50);
    }
  }, []);

  /**
   * Check if document is currently loading
   */
  const isLoading = useCallback((tabId) => {
    return loadingDocumentsRef.current.has(tabId);
  }, []);

  return {
    loadDocument,
    importBookmarks,
    setupBookmarkListener,
    restorePage,
    isLoading,
    getDocumentUrl,
    resolveDocumentUrl,
  };
};

export default useDocumentLoader;
